import type { ReactNode } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";

import { AssistantChart } from "./AssistantChart";
import { AssistantExportCard } from "./AssistantExportCard";
import { AssistantMap } from "./AssistantMap";

const toText = (node: ReactNode): string => {
  if (typeof node === "string") {
    return node;
  }
  if (Array.isArray(node)) {
    return node.map(toText).join("");
  }
  return "";
};

const components: Components = {
  // Render fenced code without the default <pre> wrapper so a chart block can
  // replace it with a real chart element.
  pre: ({ children }) => <>{children}</>,
  code: ({ className, children }) => {
    const text = toText(children).replace(/\n$/, "");
    if (className?.includes("language-tma-chart")) {
      return <AssistantChart raw={text} />;
    }
    if (className?.includes("language-tma-map")) {
      return <AssistantMap raw={text} />;
    }
    if (className?.includes("language-tma-export")) {
      return <AssistantExportCard raw={text} />;
    }
    if (className?.startsWith("language-")) {
      return (
        <pre className="my-2 overflow-x-auto rounded bg-gray-100 p-2 text-xs">
          <code>{text}</code>
        </pre>
      );
    }
    return (
      <code className="rounded bg-gray-100 px-1 py-0.5 text-[0.85em]">
        {children}
      </code>
    );
  },
  table: ({ children }) => (
    <div className="my-2 overflow-x-auto">
      <table className="w-full border-collapse text-xs">{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th className="border border-gray-200 bg-gray-50 px-2 py-1 text-left font-semibold">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border border-gray-200 px-2 py-1 align-top">{children}</td>
  ),
  a: ({ children, href }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-client-color underline"
    >
      {children}
    </a>
  ),
};

/** Renders an assistant reply as markdown (tables, lists, bold) plus charts. */
export const MarkdownMessage = ({ content }: { content: string }) => (
  <div className="text-sm leading-relaxed text-slate-800 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_blockquote]:my-2 [&_blockquote]:border-l-2 [&_blockquote]:border-[var(--assistant-accent-soft)] [&_blockquote]:pl-3 [&_blockquote]:text-slate-600 [&_h1]:mb-1 [&_h1]:mt-3 [&_h1]:text-base [&_h1]:font-bold [&_h1]:text-slate-900 [&_h2]:mb-1 [&_h2]:mt-3 [&_h2]:text-sm [&_h2]:font-bold [&_h2]:text-slate-900 [&_h3]:mb-1 [&_h3]:mt-2 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:text-slate-800 [&_hr]:my-3 [&_hr]:border-gray-200 [&_li]:pl-1 [&_li]:marker:text-[var(--assistant-accent)] [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:space-y-1 [&_ol]:pl-5 [&_p]:my-2 [&_strong]:font-semibold [&_strong]:text-slate-900 [&_ul]:my-2 [&_ul]:list-disc [&_ul]:space-y-1 [&_ul]:pl-5">
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
      {content}
    </ReactMarkdown>
  </div>
);
